import { useEffect, useMemo, useState } from "react";
import { ApiService } from "../../../../services/api";

type LessonOption = {
  value: string;
  label: string;
  meta?: any;
};

interface LessonSelectProps {
  value: string | null;
  onChange: (value: string | null, option?: LessonOption | null) => void;
  disabled?: boolean;
  className?: string;
}

export default function LessonSelect({ value, onChange, disabled, className }: LessonSelectProps) {
  const [items, setItems] = useState<LessonOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const res = await ApiService.getAllLessons();
        const raw = (res as any)?.data ?? (res as any) ?? [];
        const list: any[] = Array.isArray(raw) ? raw : [];
        const mapped: LessonOption[] = list
          .map((lesson: any) => {
            const id = String(lesson?.id || lesson?._id || lesson?.lessonId || "");
            const rawTitle = lesson?.title ?? lesson?.name ?? "";
            const title = String(rawTitle).trim() || "(Ders Bulunamadı)";
            if (!id) return null;
            return {
              value: id,
              label: title,
              meta: { lesson },
            } as LessonOption;
          })
          .filter(Boolean) as LessonOption[];
        if (active) setItems(mapped);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const selected = useMemo(() => items.find((i) => i.value === value) || null, [items, value]);

  return (
    <div className={className}>
      <div className="border border-gray-300 rounded-lg overflow-hidden">
        <div className="max-h-64 overflow-auto divide-y divide-gray-200" role="listbox" aria-label="Ders Seçiniz">
          <button
            type="button"
            onClick={() => onChange(null, null)}
            className={`w-full text-left px-3 py-2 text-sm transition-colors ${value ? "hover:bg-gray-50 text-gray-800" : "bg-blue-50 text-blue-900"}`}
            disabled={disabled || loading}>
            Ders Seçiniz
          </button>
          {items.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value, opt)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                  isSelected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                }`}
                disabled={disabled || loading}
                role="option"
                aria-selected={isSelected}>
                {opt.label}
              </button>
            );
          })}
          {!loading && items.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Ders bulunamadı.</div>}
        </div>
      </div>
      {selected && (
        <div className="sr-only" aria-live="polite">
          Seçili ders: {selected.label}
        </div>
      )}
    </div>
  );
}
